import type {IHttpResponse, IHttpService, IWindowService} from 'angular';
import * as angular from 'angular';
import CachedPermissionsDataService from '../commons/data-services/cached-permissions.data-service';
import type {Auth} from 'typings/portaro.be.types';
import type ReloaderService from '../commons/services/reloader.service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type MainLoginFormService from './main-login-form-service';

export interface LoginListener {
    update(currentAuth: Auth): Promise<void>;
}


/**
 * @ngdoc service
 * @name loginService
 * @module portaro.login
 */
export default class LoginService {
    public static serviceName = 'loginService';

    private changedButNotReloaded: boolean;
    private listeners: LoginListener[] = [];

    /*@ngInject*/
    constructor(private currentAuth: Auth,
                private mainLoginFormService: MainLoginFormService,
                private CONTEXT_PATH: string,
                private reloader: ReloaderService,
                private $window: IWindowService,
                private $http: IHttpService) {
    }

    /**
     * Refreshes current auth from backend
     */
    @ngAsync()
    public async refresh(): Promise<Auth> {
        const {data: newAuth}: IHttpResponse<Auth> = await this.$http.get(`${this.CONTEXT_PATH}/api/auth/current`);
        this.applyNewAuth(newAuth);
        return newAuth;
    }

    /**
     * This method should open login form
     */
    @ngAsync()
    public async login(): Promise<Auth> {
        if (this.currentAuth.evided) {
            return this.currentAuth;
        }

        const newAuth: Auth = await this.mainLoginFormService.openForm();
        this.applyNewAuth(newAuth);
        await this.notify(newAuth);
        return newAuth;
    }

    public registerListener(listener: LoginListener): void {
        const exist = this.listeners.includes(listener);
        if (!exist) {
            this.listeners.push(listener);
        }
    }

    /**
     * This method should re-login user
     */
    public async relogin(reloginAfterSessionTimeout: boolean = false): Promise<Auth> {
        if (this.currentAuth.evided) {
            if (reloginAfterSessionTimeout) {
                CachedPermissionsDataService.clearCache();
                this.currentAuth.evided = false;
            } else {
                await this.logout();
            }
        }
        return this.login();
    }

    @ngAsync()
    public async upgrade(): Promise<Auth> {
        await this.login();
        await this.$http.post(`${this.CONTEXT_PATH}/api/auth/multifactor/upgrade`, {});
        return this.refresh();
    }

    /**
     * Logout user
     *
     * @param {string=} path
     */
    @ngAsync()
    public async logout(path?: string): Promise<void> {
        await this.$http.get(`${this.CONTEXT_PATH}/logout`);
        CachedPermissionsDataService.clearCache();
        this.currentAuth.evided = false;
        if (angular.isDefined(path)) {
            // pokud je path "", zmenime na "/"
            this.$window.location.href = path || '/';
        } else {
            this.reloader.reload();
        }
    }

    public async goLogged(path: string) {
        try {
            await this.login();
            this.$window.location.href = path;
        } catch (e) {
            // ignore login error, do nothing
        }
    }

    public async thisPageLogged() {
        if (!this.currentAuth.evided) {
            try {
                await this.login();
                this.reloader.reload();
            } catch (e) {
                // ignore login error, do nothing
            }
        }
    }

    /**
     * This method checks if user was logged in recently and page was not reloaded
     * and if true reloads the page to update page elements dependent to user authentication
     */
    public reloadIfChangedButNotReloaded() {
        if (this.changedButNotReloaded) {
            this.reloader.reload();
        }
    }

    private async notify(auth: Auth): Promise<void> {
        for (const listener of this.listeners) {
            await listener.update(auth);
        }
    }

    private applyNewAuth(auth: Auth) {
        for (const key in auth) {
            if (Object.hasOwn(auth, key)) {
                this.currentAuth[key] = auth[key];
            }
        }
        this.changedButNotReloaded = true;
        CachedPermissionsDataService.clearCache();
    }
}
